//frontend/src/components/SuperAdmin/SuperAdminDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import { FaUsers, FaFileAlt, FaCheckCircle, FaClock, FaChartLine, FaDollarSign } from 'react-icons/fa';
import api from '../../services/api';

const SuperAdminDashboard = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalAdmins: 0,
        totalDossiers: 0,
        pendingDossiers: 0,
        approvedDossiers: 0,
        revenue: 0,
    });

    const [recentUsers, setRecentUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            // Charger les statistiques
            const [usersRes, dossiersRes] = await Promise.all([
                api.get('/admin/users'),
                api.get('/admin/dossiers/statistics'),
            ]);

            // Handle both paginated and non-paginated responses
            const users = usersRes.data.users.data || usersRes.data.users || [];
            const dossierStats = dossiersRes.data;

            setStats({
                totalUsers: users.filter(u => u.role === 'user').length,
                totalAdmins: users.filter(u => u.role === 'admin' || u.role === 'superadmin').length,
                totalDossiers: dossierStats.total || 0,
                pendingDossiers: dossierStats.submitted + dossierStats.in_review || 0,
                approvedDossiers: dossierStats.approved || 0,
                revenue: dossierStats.approved * 299 || 0, // Simulation
            });

            setRecentUsers(users.slice(0, 5));
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4">
            <h2 className="mb-4">Tableau de bord Super Admin</h2>

            {/* Stats Cards */}
            <Row className="mb-4">
                <Col md={4}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <FaUsers size={40} className="text-primary mb-3" />
                            <h3>{stats.totalUsers}</h3>
                            <p className="text-muted">Utilisateurs</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <FaUsers size={40} className="text-warning mb-3" />
                            <h3>{stats.totalAdmins}</h3>
                            <p className="text-muted">Administrateurs</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <FaFileAlt size={40} className="text-info mb-3" />
                            <h3>{stats.totalDossiers}</h3>
                            <p className="text-muted">Total Dossiers</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={4}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <FaClock size={40} className="text-danger mb-3" />
                            <h3>{stats.pendingDossiers}</h3>
                            <p className="text-muted">En attente</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <FaCheckCircle size={40} className="text-success mb-3" />
                            <h3>{stats.approvedDossiers}</h3>
                            <p className="text-muted">Approuvés</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <FaDollarSign size={40} className="text-success mb-3" />
                            <h3>{stats.revenue}€</h3>
                            <p className="text-muted">Revenus</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Recent Users */}
            <Row>
                <Col>
                    <Card>
                        <Card.Header>
                            <h5>Derniers utilisateurs inscrits</h5>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <p>Chargement...</p>
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Nom</th>
                                            <th>Email</th>
                                            <th>Rôle</th>
                                            <th>Date inscription</th>
                                            <th>Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentUsers.map((user) => (
                                            <tr key={user.id}>
                                                <td>{user.id}</td>
                                                <td>{user.first_name} {user.last_name}</td>
                                                <td>{user.email}</td>
                                                <td>
                                                    <Badge bg={
                                                        user.role === 'superadmin' ? 'danger' :
                                                        user.role === 'admin' ? 'warning' : 'info'
                                                    }>
                                                        {user.role}
                                                    </Badge>
                                                </td>
                                                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                                <td>
                                                    <Badge bg={user.is_active ? 'success' : 'secondary'}>
                                                        {user.is_active ? 'Actif' : 'Inactif'}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default SuperAdminDashboard;
